import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  IconButton,
  TablePagination,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ClipboardList,
  Clock,
  CheckCircle2,
  Banknote,
  Download,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import CollectCash from './CollectCash';
import apiService from './api';
import Confirm from './Confirm';
import Edit from './Edit';
import branchService from './branches';
import { useTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------
// The 3 status cards no longer control which dataset is loaded — only
// which status the currently-loaded dataset is filtered to (see
// matchesCard / cardStats / filteredTransactions below). What dataset is
// loaded is controlled separately by `periodMode`:
//   - 'day'    : the single day the day-switcher (‹ Today ›) is on. This
//                is the default, and clicking Pending / Confirmed while
//                here just filters that same day's data by status —
//                no extra fetch.
//   - 'month'  : the month picked via "Select Month".
//   - 'custom' : the range picked via "Custom".
// Switching Pending/Confirmed no longer triggers a fetch by itself; only
// changing periodMode (day navigation, Select Month, Custom) does. Month
// and Custom both load through fetchRangeTransactions, which now calls a
// single real backend range endpoint (start_date/end_date on
// /check-transactions/) instead of looping the single-day endpoint once
// per day and merging client-side.
// ---------------------------------------------------------------------

const STATUS_CARDS = [
  { key: 'all', label: 'All Incoming Payments', icon: ClipboardList },
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'confirmed_distributed', label: 'Confirmed / Distributed', icon: CheckCircle2 },
];

// Distributed *parent* transactions are intentionally never shown — once a
// transaction is distributed, its split children appear as their own
// "Confirmed" rows, so showing the parent too would double it up. This
// matches the original table's behavior, which hard-skipped status
// "Distributed" rows regardless of which filter was selected.
const isVisibleStatus = (status) => status !== 'Distributed';

const matchesCard = (status, cardKey) => {
  switch (cardKey) {
    case 'all': return true;
    case 'pending': return status === 'Pending';
    case 'confirmed_distributed': return status === 'Confirmed' || status === 'Distributed';
    default: return false;
  }
};

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  pending: {
    icon: (isDark) => (isDark ? 'text-orange-400' : 'text-orange-600'),
    active: (isDark) => (isDark ? 'border-orange-500 bg-orange-900/20' : 'border-orange-500 bg-orange-50'),
  },
  confirmed_distributed: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
};

// Default date range for the Pending / Confirmed-Distributed cards: past
// 45 days, as requested.
const getPast45DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 45);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

// "5 Aug 2026, 10:32:15 AM" — used for the DATE & TIME column, which only
// shows up in search mode and the range (Pending / Confirmed-Distributed)
// view, since the single-day view already gives the date via the switcher.
const formatDateTimeLabel = (isoString) => {
  const d = new Date(isoString);
  const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${datePart}, ${d.toLocaleTimeString()}`;
};

const Transactions = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchQuery, setSearchQuery] = useState("");
  const [cardFilter, setCardFilter] = useState("all"); // 'all' | 'pending' | 'confirmed_distributed' — status filter only, doesn't drive fetching
  // Which dataset is loaded: the single day the switcher is on, or a
  // month / custom range. Defaults to 'day' — Select Month / Custom are
  // opt-in, there's no default range view anymore.
  const [periodMode, setPeriodMode] = useState("day"); // 'day' | 'month' | 'custom'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [collectCashModalOpen, setCollectCashModalOpen] = useState(false);

  // New state variables for the confirm modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedTransactionForConfirm, setSelectedTransactionForConfirm] = useState(null);

  // Single-day dataset — used when periodMode === 'day'.
  const [dayTransactions, setDayTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Multi-day (range) dataset — used when periodMode is 'month' or
  // 'custom'. See the note above STATUS_CARDS.
  const [rangeTransactions, setRangeTransactions] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState(null);
  const [rangeDateRange, setRangeDateRange] = useState(getPast45DaysRange());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  const [searchResults, setSearchResults] = useState([]); // Global search results
  const [selectedBranch, setSelectedBranch] = useState(null); // null means all branches
  const [branches, setBranches] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Date navigation state (only relevant while periodMode === 'day')
  const [selectedDate, setSelectedDate] = useState(new Date());

  // New state to track if we're in search mode
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Hidden native date input the day-label button opens, so someone can
  // jump straight to any date from a calendar instead of only stepping
  // day-by-day with the arrows.
  const dateInputRef = useRef(null);

  // Reference for polling interval
  const pollingIntervalRef = useRef(null);
  // Track the latest transaction ID to detect new transactions
  const latestTransactionRef = useRef(null);

  // Debounce timeout for search
  const searchTimeoutRef = useRef(null);

  const handleCollectCashClick = () => {
    setCollectCashModalOpen(true);
  };

  // Date navigation functions (single-day / "All Incoming Payments" view)
  const goToPreviousDate = () => {
    const previousDate = new Date(selectedDate);
    previousDate.setDate(previousDate.getDate() - 1);
    setSelectedDate(previousDate);
  };

  const goToNextDate = () => {
    const nextDate = new Date(selectedDate);
    nextDate.setDate(nextDate.getDate() + 1);

    // Prevent going to future dates beyond today
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today

    if (nextDate <= today) {
      setSelectedDate(nextDate);
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Opens the browser/Electron's native calendar on the hidden date
  // input below. showPicker() is what actually pops the calendar UI;
  // falling back to focus() covers older Chromium builds that lack it.
  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch (err) {
        // falls through to focus()
      }
    }
    el.focus();
  };

  const handleDatePicked = (e) => {
    if (!e.target.value) return;
    setSelectedDate(new Date(e.target.value + 'T00:00:00'));
  };

  // Format date for API call (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Check if we can go to next date (not future dates)
  const canGoToNextDate = () => {
    const nextDate = new Date(selectedDate);
    nextDate.setDate(nextDate.getDate() + 1);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return nextDate <= today;
  };

  // Date-range handlers (Select Month / Custom). Picking either one loads
  // that period's data via fetchRangeTransactions (see the effect below).
  // Pending / Confirmed then just filter whatever period is loaded — day,
  // month, or custom — with no dataset switch of their own.
  const handleCustomDateApply = () => {
    setRangeDateRange(tempDateRange);
    setSelectedMonth('');
    setPeriodMode('custom');
    setCustomDateDialogOpen(false);
    setPage(0);
  };

  // Clears the Select Month / Custom filter and goes back to the single
  // day the switcher is on (whatever day that currently is — not
  // necessarily today).
  const handleClearPeriodFilter = () => {
    setPeriodMode('day');
    setSelectedMonth('');
    setMonthMenuAnchor(null);
    setCustomDateDialogOpen(false);
    setPage(0);
  };

  // New function to perform global search
  const performGlobalSearch = async (query) => {
    if (!query.trim()) {
      setIsSearchMode(false);
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setIsSearchMode(true);

      // Use the search API endpoint
        const response = await apiService.searchTransactions(query);

        // Extract the transactions array from the response
        const data = response.transactions || [];

        // Map Django data to frontend format with full date in time column
        const mappedData = data.map(transaction => ({
        time: formatDateTimeLabel(transaction.transaction_time),
        liftRef: transaction.company_ref || 'N/A',
        mpesaRef: transaction.transaction_id,
        sentBy: transaction.sent_by || 'Unknown',
        amount: transaction.amount,
        phoneNo: transaction.phone_number,
        sentFor: transaction.customer_name || 'N/A',
        group: transaction.member_group || 'N/A',
        status: transaction.status === 'COMPLETED'
          ? 'Confirmed'
          : transaction.status === 'DISTRIBUTED'
            ? 'Distributed'
            : 'Pending',
        transactionTime: new Date(transaction.transaction_time).getTime(),
        rawData: transaction // Keep the original data for reference
      }));

      // Sort by transaction time (newest first)
      mappedData.sort((a, b) => b.transactionTime - a.transactionTime);

      setSearchResults(mappedData);
    } catch (err) {
      console.error("Error searching transactions:", err);
      setNotification({
        open: true,
        message: `Error searching transactions: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Modified search handler with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      performGlobalSearch(query);
    }, 500); // 500ms delay
  };

  // Function to clear search and return to date/range view
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearchMode(false);
    setSearchResults([]);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Single-day fetch — runs whenever periodMode is 'day', regardless of
  // which status card (Pending/Confirmed) is selected. Same endpoint and
  // polling behavior as before.
  useEffect(() => {
    if (isSearchMode || periodMode !== 'day') return;

    fetchDayTransactions();

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Only set up polling for today's transactions
    if (isToday()) {
      pollingIntervalRef.current = setInterval(() => {
        fetchDayTransactions(true);
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line
  }, [selectedDate, selectedBranch, isSearchMode, periodMode]);

  // Range fetch — runs whenever periodMode is 'month' or 'custom',
  // regardless of which status card is selected.
  useEffect(() => {
    if (isSearchMode || periodMode === 'day') return;
    fetchRangeTransactions();
    // eslint-disable-next-line
  }, [rangeDateRange, selectedBranch, isSearchMode, periodMode]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchData = await branchService.getBranches();
        setBranches(branchData);
      } catch (error) {
        console.error('Error loading branches:', error);
      }
    };
    loadBranches();
  }, []);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchDayTransactions = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null); // Clear previous errors
      }

      // Add date parameter to API call
      const dateParam = formatDateForAPI(selectedDate);
      const data = await apiService.fetchTransactionsByDate(dateParam, selectedBranch);

      // Map Django data to frontend format with just time for date-specific view
      const mappedData = data.map(transaction => ({
        time: new Date(transaction.transaction_time).toLocaleTimeString(),
        liftRef: transaction.company_ref || 'N/A',
        mpesaRef: transaction.transaction_id,
        sentBy: transaction.sent_by || 'Unknown',
        amount: transaction.amount,
        phoneNo: transaction.phone_number,
        sentFor: transaction.customer_name || 'N/A',
        group: transaction.member_group || 'N/A',
        status: transaction.status === 'COMPLETED'
          ? 'Confirmed'
          : transaction.status === 'DISTRIBUTED'
            ? 'Distributed'
            : 'Pending',
        transactionTime: new Date(transaction.transaction_time).getTime(),
        rawData: transaction // Keep the original data for reference
      }));

      mappedData.sort((a, b) => b.transactionTime - a.transactionTime);

      // Only check for new transactions if it's today and silent fetch
      if (silent && isToday() && latestTransactionRef.current && mappedData.length > 0) {
        const newTransactions = mappedData.filter(
          transaction => transaction.transactionTime > latestTransactionRef.current
        );

        if (newTransactions.length > 0) {
          // Show notification if there are new transactions
          setNotification({
            open: true,
            message: `${newTransactions.length} new transaction${newTransactions.length > 1 ? 's' : ''} received`,
            severity: 'info'
          });
        }
      }

      // Update latest transaction timestamp reference if there are transactions and it's today
      if (mappedData.length > 0 && isToday()) {
        latestTransactionRef.current = mappedData[0].transactionTime;
      }

      setDayTransactions(mappedData);
      if (!silent) {
        setLoading(false);
      }
    } catch (err) {
      setError(err.message);
      if (!silent) {
        setLoading(false);
      }
      setNotification({
        open: true,
        message: `Error fetching transactions: ${err.message}`,
        severity: 'error'
      });
    }
  };

  // Range fetch: one real request to the backend range endpoint
  // (start_date/end_date on /check-transactions/), for whichever period
  // (Select Month or Custom) is active — no more per-day looping.
  const fetchRangeTransactions = async () => {
    try {
      setRangeLoading(true);
      setRangeError(null);

      const data = await apiService.fetchTransactionsByDateRange(
        rangeDateRange.startDate,
        rangeDateRange.endDate,
        selectedBranch
      );

      const mappedData = (data || []).map(transaction => ({
        time: formatDateTimeLabel(transaction.transaction_time),
        liftRef: transaction.company_ref || 'N/A',
        mpesaRef: transaction.transaction_id,
        sentBy: transaction.sent_by || 'Unknown',
        amount: transaction.amount,
        phoneNo: transaction.phone_number,
        sentFor: transaction.customer_name || 'N/A',
        group: transaction.member_group || 'N/A',
        status: transaction.status === 'COMPLETED'
          ? 'Confirmed'
          : transaction.status === 'DISTRIBUTED'
            ? 'Distributed'
            : 'Pending',
        transactionTime: new Date(transaction.transaction_time).getTime(),
        rawData: transaction
      }));

      mappedData.sort((a, b) => b.transactionTime - a.transactionTime);

      setRangeTransactions(mappedData);
    } catch (err) {
      console.error('Error loading range transactions:', err);
      setRangeError('Failed to load transactions for this period. Please try again.');
    } finally {
      setRangeLoading(false);
    }
  };

  // Modified handleConfirm to open the confirm modal instead of directly confirming
  const handleConfirm = async (transaction) => {
    // Instead of confirming directly, open the confirm modal
    setSelectedTransactionForConfirm(transaction);
    setConfirmModalOpen(true);
  };

  // Reload whichever dataset is currently active, after a mutation.
  const reloadActiveDataset = () => {
    if (isSearchMode) {
      performGlobalSearch(searchQuery);
    } else if (periodMode === 'day') {
      fetchDayTransactions();
    } else {
      fetchRangeTransactions();
    }
  };

  // New function to handle the actual confirmation from the modal
  const handleConfirmFromModal = async (confirmationData) => {
    try {
      await apiService.confirmTransaction(confirmationData.transaction_id, confirmationData.member_id);
      reloadActiveDataset();
      setNotification({
        open: true,
        message: 'Transaction confirmed successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error("Error confirming transaction:", err);
      setNotification({
        open: true,
        message: `Error confirming transaction: ${err.message}`,
        severity: 'error'
      });
    }
  };

  // New function to handle distribution from the edit modal
  const handleDistributeFromModal = async (distributionData) => {
    try {
      await apiService.distributeTransaction(distributionData.transaction_id, distributionData.allocations);
      reloadActiveDataset();
      setNotification({
        open: true,
        message: 'Transaction distributed successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error("Error distributing transaction:", err);
      setNotification({
        open: true,
        message: `Error distributing transaction: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleSaveTransaction = async (updatedTransaction) => {
    try {
      await apiService.updateTransaction(updatedTransaction);
      console.log('Updated transaction:', updatedTransaction);
      reloadActiveDataset();
      setNotification({
        open: true,
        message: 'Transaction updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error("Error updating transaction:", err);
      setNotification({
        open: true,
        message: `Error updating transaction: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handlePrintReceipt = async (transaction) => {
    try {
      const receiptData = await apiService.generateReceipt(transaction.mpesaRef);
      console.log('Receipt data:', receiptData);
      setNotification({
        open: true,
        message: 'Receipt generated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error("Error generating receipt:", err);
      setNotification({
        open: true,
        message: `Error generating receipt: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleDownloadStatement = async () => {
    try {
      const filters = {
        search: searchQuery,
        status: cardFilter === 'pending' ? 'pending' : null,
        branch_id: selectedBranch,
      };
      if (isSearchMode) {
        filters.date = null;
      } else if (periodMode === 'day') {
        filters.date = formatDateForAPI(selectedDate);
      } else {
        filters.start_date = rangeDateRange.startDate;
        filters.end_date = rangeDateRange.endDate;
      }
      const data = await apiService.exportTransactions(filters);
      console.log('Export data:', data);
      setNotification({
        open: true,
        message: 'Statement download initiated',
        severity: 'success'
      });
    } catch (err) {
      console.error("Error downloading statement:", err);
      setNotification({
        open: true,
        message: `Error downloading statement: ${err.message}`,
        severity: 'error'
      });
    }
  };

  // Which raw dataset backs the page right now — driven by periodMode, not
  // by cardFilter, so clicking Pending/Confirmed never changes what's
  // loaded, only how it's filtered.
  const activeRawDataset = isSearchMode ? searchResults : (periodMode === 'day' ? dayTransactions : rangeTransactions);

  // Distributed parents are always hidden (see isVisibleStatus) — this is
  // the base every card count and the table itself are computed from.
  const visibleDataset = useMemo(
    () => activeRawDataset.filter(t => isVisibleStatus(t.status)),
    [activeRawDataset]
  );

  // Card counts + totals reflect whichever dataset is currently loaded —
  // the single day the switcher is on, or the selected month/custom range.
  // Switching cards never changes what's fetched (only periodMode does —
  // see the two useEffects above), so these numbers are always grounded in
  // real, currently-loaded data rather than guessed.
  const cardStats = useMemo(() => {
    return STATUS_CARDS.map(card => {
      const matching = visibleDataset.filter(t => matchesCard(t.status, card.key));
      const total = matching.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      return { ...card, count: matching.length, total };
    });
  }, [visibleDataset]);

  const filteredTransactions = useMemo(() => {
    return visibleDataset
      .filter(t => matchesCard(t.status, cardFilter))
      .filter(t => {
        // In search mode, don't apply additional text filtering since
        // search is already done server-side. Otherwise this doubles as
        // an instant local pre-filter while the debounced global search
        // is still pending.
        if (isSearchMode || !searchQuery) return true;
        return Object.values(t).some((value) =>
          value && typeof value === 'string' &&
          value.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
  }, [visibleDataset, cardFilter, isSearchMode, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setEditModalOpen(true);
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Format selected date for display (single-day / "All" view)
  const formattedDay = selectedDate.toLocaleDateString(undefined, { weekday: 'long' });
  const formattedDate = selectedDate.toLocaleDateString();

  // Short label for the range view's subtitle, e.g. "9 Aug 26 – 23 Sep 26"
  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: '2-digit',
    });
  };

  const isPageLoading = isSearchMode ? isSearching : (periodMode === 'day' ? loading : rangeLoading);
  const pageError = isSearchMode ? null : (periodMode === 'day' ? error : rangeError);

  const headers = [
    (isSearchMode || periodMode !== 'day') ? "DATE & TIME" : "TIME",
    "MPESA REF",
    "SENT BY",
    "AMOUNT",
    "PHONE NO.",
    "SENT FOR",
    "GROUP",
    "STATUS",
    "ACTION"
  ];

  // Helper function to get status color — unchanged brand colors.
  const getStatusColor = (status, mpesaRef) => {
    // Check if this is a distributed child transaction (has exactly one underscore)
    const underscoreCount = (mpesaRef.match(/_/g) || []).length;

    if (status === 'Confirmed' && underscoreCount === 1) {
      return '#2196f3'; // Blue color for distributed child transactions
    }

    switch (status) {
      case 'Confirmed':
        return '#a7ce35';
      case 'Distributed':
        return '#2196f3';
      case 'Pending':
      default:
        return '#FFA500';
    }
  };

  return (
    <div className="pb-32">
      {/* Status cards - All Incoming Payments / Pending / Confirmed-Distributed */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = cardFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => {
                setCardFilter(card.key);
                setPage(0);
              }}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                KSh {card.total.toLocaleString()}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Incoming Payments
              {!isSearchMode && periodMode === 'day' && isToday() && (
                <span className="text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-600 text-white">
                  Today
                </span>
              )}
            </h2>
            {isSearchMode ? (
              <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {`${filteredTransactions.length} transaction${filteredTransactions.length !== 1 ? 's' : ''} found`}
              </p>
            ) : periodMode === 'day' ? (
              // Day switcher — lives here, under the title, instead of in
              // the filters row, since it's really the page's date context
              // rather than a filter. Clicking Pending / Confirmed above
              // filters *this* day's data, it doesn't change it.
              <div className="relative flex items-center gap-1 mt-1.5 -ml-2">
                <IconButton onClick={goToPreviousDate} size="small">
                  <ChevronLeft className={isDark ? 'text-gray-300' : 'text-gray-700'} />
                </IconButton>
                <button
                  onClick={openDatePicker}
                  title="Click to pick a date"
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-medium ${
                    isToday() ? 'bg-blue-600 text-white' : isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  {formattedDay}, {formattedDate}
                </button>
                {/* Invisible but present in the DOM so showPicker()/focus()
                    can open it — this is what actually renders the
                    calendar. */}
                <input
                  ref={dateInputRef}
                  type="date"
                  value={formatDateForAPI(selectedDate)}
                  max={formatDateForAPI(new Date())}
                  onChange={handleDatePicked}
                  className="absolute left-0 top-0 w-px h-px opacity-0 pointer-events-none"
                  // The native calendar popup follows color-scheme, not the
                  // app's own isDark state — without this it always renders
                  // light, even in dark mode.
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <IconButton onClick={goToNextDate} disabled={!canGoToNextDate()} size="small">
                  <ChevronRight className={canGoToNextDate() ? (isDark ? 'text-gray-300' : 'text-gray-700') : 'text-gray-400'} />
                </IconButton>
                {!isToday() && (
                  <button
                    onClick={goToToday}
                    className={`ml-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${
                      isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                    }`}
                  >
                    Today
                  </button>
                )}
              </div>
            ) : (
              // Select Month / Custom is active — this replaces the day
              // switcher in the same spot, showing the period instead of
              // today's date, until the filter is cleared with the X.
              <div className="relative flex items-center gap-1 mt-1.5 -ml-2">
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-medium bg-blue-600 text-white">
                  <Calendar className="w-4 h-4" />
                  {periodMode === 'month' && selectedMonth
                    ? (() => {
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
                        const [year, monthIndex] = selectedMonth.split('-');
                        return `${monthNames[parseInt(monthIndex)]} ${year}`;
                      })()
                    : `${formatShortDate(rangeDateRange.startDate)} – ${formatShortDate(rangeDateRange.endDate)}`}
                </span>
                <button
                  onClick={handleClearPeriodFilter}
                  title="Clear filter and go back to today's view"
                  className={`ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${
                    isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                  }`}
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCollectCashClick}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-[#a7ce35] hover:bg-[#96ba2f] transition-colors"
            >
              <Banknote className="w-4 h-4" />
              Record Cash
            </button>
            <button
              onClick={handleDownloadStatement}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              }`}
            >
              <Download className="w-4 h-4" />
              Download Statement
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Narrower now that Select Month / Custom sit to its right —
              it no longer needs to fill the row on its own. */}
          <div className="relative w-full sm:w-80">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={handleSearchChange}
              className={`w-full pl-10 pr-9 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                title="Clear search"
                className={`absolute right-2.5 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'}`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Branch Filter */}
          <select
            value={selectedBranch || ''}
            onChange={(e) => setSelectedBranch(e.target.value === '' ? null : parseInt(e.target.value))}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            {branches.map((branch) => (
              <option key={branch.id || 'all'} value={branch.id || ''}>
                {branch.name}
              </option>
            ))}
          </select>

          {/* Select Month / Custom — pushed to the right of the row.
              Hidden only while searching (search spans all dates anyway).
              Picking either sets periodMode to 'month'/'custom', which
              loads that period and replaces the day switcher up in the
              title with the period label until cleared. */}
          {!isSearchMode && (
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
                className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
                  periodMode === 'month'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : isDark
                      ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <span>
                  {periodMode === 'month' && selectedMonth !== '' ? (() => {
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const [year, monthIndex] = selectedMonth.split('-');
                    return `${monthNames[parseInt(monthIndex)]} ${year}`;
                  })() : 'Select Month'}
                </span>
                <Calendar className="w-4 h-4 ml-2" />
              </button>

              <button
                onClick={() => {
                  setTempDateRange(periodMode === 'custom' ? rangeDateRange : getPast45DaysRange());
                  setCustomDateDialogOpen(true);
                }}
                className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  periodMode === 'custom'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : isDark
                      ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <Calendar className="w-4 h-4" />
                Custom
              </button>
            </div>
          )}
        </div>

        {/* Error banner */}
        {pageError && !isPageLoading && (
          <div className={`mx-5 mt-4 rounded-lg p-4 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between gap-3">
              <p className={isDark ? 'text-red-400' : 'text-red-600'}>{pageError}</p>
              <button
                onClick={reloadActiveDataset}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 shrink-0"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {isPageLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {isSearchMode ? 'Searching transactions...' : periodMode === 'day' ? `Loading transactions for ${formattedDate}...` : 'Loading transactions for this period...'}
            </span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <tr>
                    {headers.map((header) => (
                      <th
                        key={header}
                        className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((transaction, index) => {
                      const isActionable = transaction.status !== 'Confirmed' && transaction.status !== 'Distributed';
                      const displayStatus = transaction.status === 'Confirmed' && transaction.mpesaRef.split('_').length === 2
                        ? 'Confirmed'
                        : transaction.status;
                      return (
                        <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {transaction.time}
                          </td>
                          <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {transaction.mpesaRef}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {transaction.sentBy}
                          </td>
                          <td className={`px-6 py-4 font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {parseFloat(transaction.amount).toLocaleString()}
                          </td>
                          <td className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {transaction.phoneNo}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {transaction.sentFor !== 'N/A' ? transaction.sentFor.split(' ').slice(0, 2).join(' ') : 'N/A'}
                          </td>
                          <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {transaction.group !== 'N/A'
                              ? (transaction.group.length > 13
                                  ? transaction.group.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()).substring(0, 13) + '..'
                                  : transaction.group.toLowerCase().replace(/\b\w/g, l => l.toUpperCase()))
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white"
                              style={{ backgroundColor: getStatusColor(transaction.status, transaction.mpesaRef) }}
                            >
                              {displayStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleConfirm(transaction)}
                                disabled={!isActionable}
                                className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => handleEditClick(transaction)}
                                disabled={!isActionable}
                                className={`px-3 py-1.5 border text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                  isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                                }`}
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {filteredTransactions.length === 0 && (
              <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <p className="text-lg">
                  {isSearchMode
                    ? `No transactions found matching "${searchQuery}"`
                    : periodMode === 'day'
                      ? `No transactions found for ${formattedDate}`
                      : 'No transactions found for this period.'}
                </p>
              </div>
            )}

            {/* Footer pagination — kept as MUI's TablePagination (rows-per-page
                included) since it's working functionality, not part of the
                page's visual redesign. */}
            <Box className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredTransactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ color: isDark ? '#e5e7eb' : undefined }}
              />
            </Box>
          </>
        )}
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setRangeDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setPeriodMode('month');
                    setMonthMenuAnchor(null);
                    setPage(0);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearPeriodFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear filter (back to today's view)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearPeriodFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals — unchanged, working business logic. */}
      <Confirm
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        transactionData={selectedTransactionForConfirm}
        onConfirm={handleConfirmFromModal}
      />

      <Edit
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        transactionData={selectedTransaction}
        onConfirm={handleDistributeFromModal}
      />

      <CollectCash
        open={collectCashModalOpen}
        onClose={() => setCollectCashModalOpen(false)}
        onTransactionComplete={() => reloadActiveDataset()}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Transactions;